import { extractConfig, applyServerDefaults, type WidgetConfig } from './config'
import { fetchWidgetConfig, type StaffPublicConfig } from './api/backend'
import { HolostaffWidget } from './widget'
import { normalizePatterns, findMatch, SPARouter } from './routing/pageTargeting'
import { RRWebRecorder } from './vision/rrwebRecorder'
import { V1WsClient } from './vision/v1WsClient'
const DEFAULT_V1_WS_URL = 'wss://holostaff-vision-1008066443043.us-central1.run.app/v1/widget'
import { HolostaffSdk, installGlobal as installSdkGlobal } from './sdk/holostaffSdk'

// Helper: generate or retrieve persistent visitor ID
function getOrCreateVisitorId(): string {
  const COOKIE_NAME = 'hs_visitor_id'
  const existing = document.cookie
    .split('; ')
    .find(c => c.startsWith(`${COOKIE_NAME}=`))
    ?.split('=')[1]

  if (existing) return existing

  const id = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
  document.cookie = `${COOKIE_NAME}=${id}; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Lax`
  return id
}

// Auto-bootstrap: find the script tag that loaded us and extract config
function bootstrap(): void {
  const scriptEl = document.currentScript as HTMLScriptElement | null
    ?? document.querySelector<HTMLScriptElement>('script[data-staff-id]')

  if (!scriptEl) {
    console.error('[HolostaffWidget] Could not find script tag with data-staff-id')
    return
  }

  try {
    const config = extractConfig(scriptEl)

    async function initWidget() {
      let finalConfig = config
      let widgetPrompts: string[] | null = null
      let serverConfig: StaffPublicConfig | null = null

      // Eval mode: check window.__HS_EVAL__ (set by extension or addInitScript),
      // AND as a fallback the URL hash (#__hs_eval=<encoded JSON>). The hash
      // path is self-contained — doesn't depend on extension loading or script
      // injection timing — so evals work even when other injection paths miss.
      let evalConfig = (window as any).__HS_EVAL__ as { staffId: string; visitorId: string; mode: string } | undefined
      if (!evalConfig) {
        try {
          const hashMatch = location.hash.match(/(?:^#|&)__hs_eval=([^&]+)/)
          if (hashMatch) {
            evalConfig = JSON.parse(decodeURIComponent(hashMatch[1]))
            ;(window as any).__HS_EVAL__ = evalConfig
            // Strip eval param from URL so it doesn't pollute analytics
            try {
              const cleanHash = location.hash.replace(/(?:^#|&)__hs_eval=[^&]+/, '').replace(/^&/, '#')
              const newUrl = location.pathname + location.search + (cleanHash === '#' || cleanHash === '' ? '' : cleanHash)
              history.replaceState(null, '', newUrl)
            } catch { /* non-fatal */ }
            console.log('[HolostaffWidget] Eval config read from URL hash', evalConfig)
          }
        } catch { /* non-fatal */ }
      }

      // Resolve visitor identity before config fetch so the block check can run
      const visitorId = evalConfig?.visitorId ?? getOrCreateVisitorId()

      // Initialise the Holostaff SDK (I-006). The SDK installs itself as
      // window.Holostaff and drains any Segment-style pre-load queue.
      // Ingest base URL is derived from visionWsUrl by swapping protocols
      // (ws/wss → http/https). Widgets without vision still get the SDK
      // but events will no-op until visionWsUrl is present.
      if (config.visionWsUrl) {
        const ingestBase = config.visionWsUrl.replace(/^ws(s?):/, 'http$1:').replace(/\/$/, '')
        const sdk = new HolostaffSdk({
          staffId: config.staffId,
          visitorId,
          ingestBaseUrl: ingestBase,
          library: { name: 'holostaff-widget', version: __WIDGET_VERSION__ },
          piiMode: (scriptEl!.dataset.piiMode as 'hashed' | 'raw') ?? 'hashed',
          respectDnt: scriptEl!.dataset.respectDnt !== 'false',
        })
        installSdkGlobal(sdk)
      }

      // Fetch server config to get RTDB-stored defaults + widgetPrompts + pagePatterns
      try {
        serverConfig = await fetchWidgetConfig(config.staffId, config.backendUrl, visitorId)

        // Visitor is flagged — suppress the widget entirely. No rrweb, no vision WS,
        // no avatar, no DOM footprint whatsoever.
        if (serverConfig.blocked) {
          console.log('[HolostaffWidget] Visitor blocked — widget suppressed.')
          return
        }

        finalConfig = applyServerDefaults(config, scriptEl!, serverConfig)
        widgetPrompts = serverConfig.widgetPrompts

        // Eval mode: expose __HS_EVAL_STATE__ for the eval runner to inspect
        if (serverConfig.evalMode) {
          ;(window as any).__HS_EVAL_STATE__ = {
            interventionFired: false,
            skillId: null,
            openingMessage: null,
            conversationStarted: false,
            conversationEnded: false,
            avatarTranscripts: [],
          }
          console.log('[HolostaffWidget] Eval mode active — __HS_EVAL_STATE__ initialized')
        }
      } catch (err) {
        if ((err as any).name === 'WidgetDisabledError') {
          // Agent deleted or inactive — nothing should appear on the host page
          console.log('[HolostaffWidget] Agent is not active — widget suppressed')
          return
        }
        console.warn('[HolostaffWidget] Failed to fetch server config, using embed defaults:', err)
      }

      // --- Issue 26: Start observer layer on ALL pages ---
      const patterns = normalizePatterns(serverConfig?.pagePatterns)
      let recorder: RRWebRecorder | null = null
      let visionWs: V1WsClient | null = null

      if (finalConfig.v1Mode) {
        // v1 Bowtie orchestrator path — uses the new /v1/widget WS. PRD
        // §2.3 makes vision the canonical perception engine, so we run
        // rrweb capture alongside the SDK heartbeat and stream both
        // over /v1/widget. Server's per-session vision pipeline replays
        // rrweb in Browserbase, runs the VLM, and writes derived
        // signals + salience notes into the same bowtie memory the
        // orchestrator reads from.
        const wsUrl = finalConfig.v1WsUrl ?? DEFAULT_V1_WS_URL
        console.log('[HolostaffWidget] v1 mode — connecting to', wsUrl)
        visionWs = new V1WsClient(wsUrl, {
          staffId: finalConfig.staffId,
          visitorId,
          tenantId: finalConfig.tenantId ?? finalConfig.staffId,
          pageUrl: location.href,
          pageTitle: document.title,
          pagePatterns: patterns.map(p => {
            const entry: { pattern: string; container?: string } = { pattern: p.pattern }
            if (p.container) entry.container = p.container
            return entry
          }),
        })

        recorder = new RRWebRecorder()
        recorder.onEvent = (event) => {
          visionWs?.sendRRWebEvent(event)
        }

        visionWs.connect()

        if (finalConfig.visionConsent === 'required') {
          console.log('[HolostaffWidget] v1 observer started (recording deferred — consent required)')
        } else {
          recorder.start()
          console.log('[HolostaffWidget] v1 observer started', { visitorId })
        }
      }

      // --- Page targeting (Issue 22): presenter only on matched pages ---
      let currentMatch = findMatch(location.pathname, patterns)
      let widget: HolostaffWidget | null = null

      if (currentMatch) {
        let cfg = finalConfig
        if (currentMatch.container) {
          cfg = { ...finalConfig, mode: 'inline', container: currentMatch.container }
        }
        widget = createWidget(cfg, serverConfig, widgetPrompts, recorder, visionWs)
        ;(window as any).holostaffWidget = widget
        widget.init()
      } else {
        console.log('[HolostaffWidget] No matching page pattern for', location.pathname, '— presenter not loaded (observer active)')
      }

      // --- SPA route change detection ---
      const spaRouter = new SPARouter((newPath) => {
        // Always notify the vision server about page changes (Issue 26)
        visionWs?.sendPageChanged(location.href, newPath, document.title)

        const newMatch = findMatch(newPath, patterns)

        if (newMatch && !widget) {
          // First time landing on a targeted page — create presenter
          console.log('[HolostaffWidget] SPA navigated to matching page:', newPath)
          let cfg = finalConfig
          if (newMatch.container) {
            cfg = { ...finalConfig, mode: 'inline', container: newMatch.container }
          }
          widget = createWidget(cfg, serverConfig, widgetPrompts, recorder, visionWs)
          ;(window as any).holostaffWidget = widget
          widget.init()
        } else if (newMatch && widget) {
          // Already have a presenter — update container if needed, resume
          const newContainer = newMatch.container ?? null
          if (newContainer !== widget.activeContainer) {
            widget.updateContainer(newContainer)
          }
          widget.resume()
        } else if (!newMatch && widget) {
          // Left a targeted page — hide presenter (observer keeps running)
          widget.pause()
        }
      })

      ;(window as any).__holostaffSPARouter = spaRouter
    }

    function createWidget(
      cfg: WidgetConfig,
      serverConfig: StaffPublicConfig | null,
      widgetPrompts: string[] | null,
      recorder: RRWebRecorder | null,
      visionWs: V1WsClient | null,
    ): HolostaffWidget {
      const widget = new HolostaffWidget(cfg, widgetPrompts, serverConfig?.evalMode)

      // Attach external observer. v3 legacy has (recorder + visionWs);
      // Wires the v1 observer (rrweb + V1WsClient) into ActionExecutor.
      // the widget's ActionExecutor to the right transport.
      if (visionWs) {
        widget.setObserver(recorder, visionWs)
      }

      if (serverConfig?.idleVideoUrl) widget.setIdleVideo(serverConfig.idleVideoUrl)
      else if (serverConfig?.avatarImageUrl) widget.setIdleImage(serverConfig.avatarImageUrl)

      if (serverConfig) {
        widget.setStaffInfo(serverConfig.name, serverConfig.avatarImageUrl)
      }

      return widget
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initWidget())
    } else {
      initWidget()
    }
  } catch (err) {
    console.error('[HolostaffWidget]', err)
  }
}

bootstrap()

export { HolostaffWidget }
export type { WidgetConfig }
